import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import useFirstVisit from '../hooks/useFirstVisit';
import './DashboardTutorial.css';

const SCHRITTE = [
  {
    targetId: 'tutorial-se-url',
    optional: false,
    pad: 12,
    radius: 12,
    titel: 'Hier gibst du deinen Link ein',
    inhalt: (
      <p>
        Füge den Link zur Stellenanzeige ein — CVFlow liest die Anzeige
        automatisch aus und bereitet alles für die Analyse vor. URLs von
        LinkedIn, Indeed, Stepstone & Co. funktionieren am besten.
      </p>
    ),
    tooltipPos: 'bottom',
  },
  {
    targetId: 'tutorial-se-gelesen',
    optional: true,
    pad: 12,
    radius: 12,
    titel: 'Stelle erfolgreich ausgelesen',
    inhalt: (
      <p>
        CVFlow hat die Stellenanzeige gelesen und den Text extrahiert.
        Du kannst ihn hier einsehen, bevor du die Analyse startest.
      </p>
    ),
    tooltipPos: 'bottom',
  },
  {
    targetId: 'tutorial-se-quellen',
    optional: false,
    pad: 12,
    radius: 14,
    titel: 'Quelle wählen',
    inhalt: (
      <p>
        Du hast drei Möglichkeiten: <strong>URL verwenden</strong> (empfohlen),{' '}
        <strong>Text einfügen</strong> für kopierte Anzeigen, oder{' '}
        <strong>PDF importieren</strong> wenn die Stelle als Datei vorliegt.
      </p>
    ),
    tooltipPos: 'top',
  },
  {
    targetId: 'tutorial-se-templates',
    optional: false,
    pad: 12,
    radius: 14,
    titel: 'Vorlage aussuchen',
    inhalt: (
      <p>
        Hier wählst du das Design für deinen Lebenslauf. Die Vorlage
        bestimmt das Layout deiner fertigen Unterlagen.
      </p>
    ),
    tooltipPos: 'left',
  },
  {
    targetId: 'tutorial-se-analyse',
    optional: false,
    pad: 10,
    radius: 12,
    titel: 'Analyse starten',
    inhalt: (
      <p>
        Sobald du deine Vorlage ausgewählt hast, klickst du hier — CVFlow
        analysiert die Stelle und erstellt passende Unterlagen für dich.
      </p>
    ),
    tooltipPos: 'top',
  },
];

function useRect(targetId) {
  const [rect, setRect] = useState(null);
  const rafRef = useRef(null);

  useEffect(() => {
    function tick() {
      const el = document.getElementById(targetId);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect(prev => {
          if (!prev || prev.top !== r.top || prev.left !== r.left ||
              prev.width !== r.width || prev.height !== r.height) {
            return { top: r.top, left: r.left, width: r.width, height: r.height,
                     bottom: r.bottom, right: r.right };
          }
          return prev;
        });
      } else {
        setRect(null);
      }
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [targetId]);

  return rect;
}

const TOOLTIP_W = 320;

function TutorialOverlay({ schritt, onWeiter, onUeberspringen, gesamt, onSkipStep }) {
  const step = SCHRITTE[schritt];
  const { targetId, pad, radius, titel, inhalt, tooltipPos, optional } = step;
  const rect = useRect(targetId);

  // Optionaler Schritt — Element existiert nicht → automatisch weiter
  useEffect(() => {
    if (optional && rect === null) {
      const t = setTimeout(onSkipStep, 80);
      return () => clearTimeout(t);
    }
  }, [optional, rect, onSkipStep]);

  if (!rect) return null;

  const top    = rect.top    - pad;
  const left   = rect.left   - pad;
  const width  = rect.width  + pad * 2;
  const height = rect.height + pad * 2;

  let ttStyle = {};
  if (tooltipPos === 'bottom') {
    ttStyle = {
      top:  rect.bottom + pad + 16,
      left: rect.left + rect.width / 2 - TOOLTIP_W / 2,
    };
  } else if (tooltipPos === 'top') {
    ttStyle = {
      bottom: window.innerHeight - rect.top + pad + 16,
      left:   rect.left + rect.width / 2 - TOOLTIP_W / 2,
    };
  } else if (tooltipPos === 'left') {
    ttStyle = {
      top:   rect.top + rect.height / 2 - 80,
      right: window.innerWidth - rect.left + pad + 16,
    };
  }
  if (ttStyle.left !== undefined) {
    ttStyle.left = Math.max(16, Math.min(ttStyle.left, window.innerWidth - TOOLTIP_W - 16));
  }

  // Sichtbare Schrittnummer (optionale Schritte nicht zählen)
  const sichtbareSchritte = SCHRITTE.filter(s => !s.optional).length + (optional ? 0 : 0);
  const gesamtSichtbar = SCHRITTE.filter(s => !s.optional).length + SCHRITTE.filter(s => s.optional).length;

  return createPortal(
    <div className="dt-root" onClick={e => e.stopPropagation()}>
      <div className="dt-backdrop" />
      <div
        className="dt-spotlight"
        style={{ top, left, width, height, borderRadius: radius }}
      />
      <div className="dt-tooltip" style={{ ...ttStyle, width: TOOLTIP_W }}>
        <div className="dt-tooltip-step">{schritt + 1} / {gesamt}</div>
        <h3 className="dt-tooltip-titel">{titel}</h3>
        <div className="dt-tooltip-inhalt">{inhalt}</div>
        <div className="dt-tooltip-actions">
          <button className="dt-skip-inline" onClick={onUeberspringen}>
            Überspringen
          </button>
          <button className="dt-weiter" onClick={onWeiter}>
            {schritt === gesamt - 1 ? 'Fertig ✓' : 'Weiter →'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function StellenEditorTutorial() {
  const { isFirst, dismiss } = useFirstVisit('stellen-editor');
  const [schritt, setSchritt] = useState(0);

  if (!isFirst) return null;

  function weiter() {
    if (schritt < SCHRITTE.length - 1) setSchritt(s => s + 1);
    else dismiss();
  }

  return (
    <TutorialOverlay
      schritt={schritt}
      onWeiter={weiter}
      onUeberspringen={dismiss}
      onSkipStep={weiter}
      gesamt={SCHRITTE.length}
    />
  );
}
